import { InsightsApi } from '@insights/insights-data-provider';
import analytics from 'analytics-api';
import AnalyticsService from 'analytics-api/AnalyticsService';
import ConsoleService from 'analytics-api/dev-utils/ConsoleService';
import InsightsService from 'analytics-api/insights/InsightsService';
import OptimizelyService from 'analytics-api/optimizely/OptimizelyService';
import withInteraction from 'analytics-api/payload-transformers/withInteraction';
import withLocation from 'analytics-api/payload-transformers/withLocation';
import { CookieConsentLevels, useCookieConsent } from 'contexts/cookieConsent';
import { withInsights } from 'contexts/insights';
import { useServiceSettings } from 'contexts/serviceSettings';
import initErrorReporting from 'error-reporting';
import React, { useCallback, useEffect, useState } from 'react';
import { getKioskId, getStoreId, isProduction } from 'utils/platform';
import { getQuery } from 'utils/queryUtils';
import { useCore } from 'contexts/core';
import withSceneArticles from 'analytics-api/payload-transformers/withSceneArticles';
import { useTestFeature } from 'optimizely/hooks';
const forceEnableSentry = getQuery().get('forceEnableSentry') === 'true';

const initSentry = (sentryReplayLoggingEnabled?: boolean) => {
  const sentryRelease = import.meta.env.REACT_APP_SENTRY_RELEASE;

  if (sentryRelease || forceEnableSentry) {
    if (!sentryRelease) {
      initErrorReporting('forced', undefined, sentryReplayLoggingEnabled);
    } else {
      initErrorReporting(sentryRelease, undefined, sentryReplayLoggingEnabled);
    }
  } else {
    // eslint-disable-next-line no-console
    console.info('Error reporting service is not configured!');
  }
};

type Props = {
  children: React.ReactElement;
  insightsApi: InsightsApi;
};

const InitializeAnalytics = ({ children, insightsApi }: Props) => {
  const {
    kompisSettings: { locale, applicationName }
  } = useServiceSettings();
  const [initialized, setInitialized] = useState(false);
  const { hasCookieConsent } = useCookieConsent();
  const storeId = getStoreId();
  const kioskId = getKioskId();
  const coreApi = useCore();
  const sentryReplayLoggingEnabled = useTestFeature('SentryReplayLogging');

  const initializeAnalytics = useCallback(async () => {
    if (initialized) {
      return;
    }

    analytics.configure({
      payloadTransformers: [withInteraction, withSceneArticles, withLocation],
      coreApi
    });

    // We don't initialize sentry in dev mode
    // Force enable sentry by using the query param
    if (isProduction() || forceEnableSentry) {
      initSentry(sentryReplayLoggingEnabled);
    }

    const verboseAnalyticsLogging = getQuery().get('verboseAnalyticsLogging') === 'true';

    const services: AnalyticsService[] = [new ConsoleService(verboseAnalyticsLogging)];
    const hasConsent = await hasCookieConsent(CookieConsentLevels.ANALYTICAL);
    if (hasConsent) {
      services.push(new OptimizelyService());
      services.push(new InsightsService(insightsApi, locale, applicationName, storeId, kioskId));
    }
    // TODO [STOR-3867] implement Sentry service
    // NOTE: The order matters! Optimizely must be initialized before Insights
    await analytics.connect(...services);
    setInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationName, hasCookieConsent, initialized, locale]);

  useEffect(() => {
    initializeAnalytics();
  }, [initializeAnalytics]);

  if (!initialized) {
    return null;
  }

  return children;
};

export default withInsights(InitializeAnalytics);
